import type { SVGProps } from 'react'

import {
  HUEBO_LOGO_MOCK_DEFAULT_GEOMETRY,
  HueboVoxelLogoMock,
  buildHueboVoxelLogoMockSvg,
  type HueboVoxelLogoMockBaseColors,
  type HueboVoxelLogoMockGeometry,
  type HueboVoxelLogoMockRenderMode,
} from '@/components/HueboVoxelLogoMock'

type FaceColors = {
  top: string
  left: string
  right: string
  seam: string
}

export type LogoColorSlot = 'background' | 'leftColumn' | 'rightColumn' | 'bridge' | 'center' | 'shadow'

export type HueboLogoColors = Record<LogoColorSlot, string>

export type HueboVoxelLogoColors = {
  tile: string
  tileStroke: string
  leftColumn: FaceColors
  rightColumn: FaceColors
  bridge: FaceColors
  bridgeCenter: FaceColors
}

type HueboVoxelLogoColorOverrides = Partial<{
  [Key in keyof HueboVoxelLogoColors]: HueboVoxelLogoColors[Key] extends FaceColors
    ? Partial<FaceColors>
    : HueboVoxelLogoColors[Key]
}>

type HueboVoxelLogoColorInput = HueboVoxelLogoColorOverrides | Partial<HueboLogoColors>

export type HueboVoxelLogoGeometry = HueboVoxelLogoMockGeometry

export type HueboVoxelLogoProps = Omit<SVGProps<SVGSVGElement>, 'color'> & {
  colors?: HueboVoxelLogoColorInput
  geometry?: Partial<HueboVoxelLogoGeometry>
  renderMode?: HueboVoxelLogoMockRenderMode
  strokeWidth?: number
  lineArtFill?: string
  showTile?: boolean
  title?: string
}

export const HUEBO_LOGO_DEFAULT_COLORS: HueboLogoColors = {
  background: '#172327',
  leftColumn: '#38d8cb',
  rightColumn: '#46cfc3',
  bridge: '#ffb739',
  center: '#ff943f',
  shadow: '#0a171b',
}

const DEFAULT_COLORS: HueboVoxelLogoColors = {
  tile: HUEBO_LOGO_DEFAULT_COLORS.background,
  tileStroke: '#31484d',
  leftColumn: {
    top: '#7cf7ea',
    left: HUEBO_LOGO_DEFAULT_COLORS.leftColumn,
    right: '#259b97',
    seam: '#53ddd2',
  },
  rightColumn: {
    top: '#83f2e7',
    left: HUEBO_LOGO_DEFAULT_COLORS.rightColumn,
    right: '#2c9a94',
    seam: '#4fd7c6',
  },
  bridge: {
    top: '#ffd96d',
    left: HUEBO_LOGO_DEFAULT_COLORS.bridge,
    right: '#eb842d',
    seam: '#ffc154',
  },
  bridgeCenter: {
    top: '#ffc45f',
    left: HUEBO_LOGO_DEFAULT_COLORS.center,
    right: '#e15f34',
    seam: '#ffac58',
  },
}

export const hueboVoxelLogoDefaults = {
  colors: DEFAULT_COLORS,
  geometry: HUEBO_LOGO_MOCK_DEFAULT_GEOMETRY,
} as const

export function HueboVoxelLogo({
  colors: colorOverrides,
  geometry: geometryOverrides,
  renderMode,
  strokeWidth,
  lineArtFill,
  showTile,
  title,
  ...svgProps
}: HueboVoxelLogoProps) {
  const baseColors = resolveLogoBaseColors(colorOverrides)
  const includeTile = showTile ?? false

  return (
    <HueboVoxelLogoMock
      baseColors={baseColors}
      geometry={geometryOverrides}
      renderMode={renderMode}
      strokeWidth={strokeWidth}
      lineArtFill={lineArtFill}
      showTile={includeTile}
      title={title}
      {...svgProps}
    />
  )
}

export function resolveLogoColors(colors: Partial<HueboLogoColors> | undefined): HueboLogoColors {
  return { ...HUEBO_LOGO_DEFAULT_COLORS, ...colors }
}

export function buildHueboLogoSvg(
  colors: Partial<HueboLogoColors> = HUEBO_LOGO_DEFAULT_COLORS,
  options: {
    geometry?: Partial<HueboVoxelLogoGeometry>
    renderMode?: HueboVoxelLogoMockRenderMode
    strokeWidth?: number
    lineArtFill?: string
    showTile?: boolean
    title?: string
  } = {},
): string {
  const resolvedColors = resolveLogoColors(colors)
  return buildHueboVoxelLogoMockSvg(
    {
      columns: resolvedColors.leftColumn,
      bridge: resolvedColors.bridge,
    },
    options,
  )
}

function resolveLogoBaseColors(colors: HueboVoxelLogoColorInput | undefined): HueboVoxelLogoMockBaseColors {
  if (isFlatLogoColors(colors)) {
    const resolvedColors = resolveLogoColors(colors)
    return {
      columns: resolvedColors.leftColumn,
      bridge: resolvedColors.bridge,
    }
  }

  return {
    columns: colors?.leftColumn?.left ?? HUEBO_LOGO_DEFAULT_COLORS.leftColumn,
    bridge: colors?.bridge?.left ?? colors?.bridgeCenter?.left ?? HUEBO_LOGO_DEFAULT_COLORS.bridge,
  }
}

function isFlatLogoColors(colors: HueboVoxelLogoColorInput | undefined): colors is Partial<HueboLogoColors> {
  if (!colors) return false
  return (
    typeof colors.leftColumn === 'string'
    || ('background' in colors && typeof colors.background === 'string')
    || ('shadow' in colors && typeof colors.shadow === 'string')
  )
}
